import debounce from "lodash/debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoanProcessorsRepository } from "@/api/loan-processors/repository";
import { LoanProcessorsService } from "@/api/loan-processors/service";
import { useAppStore } from "@/store/app/slice";
import type { ILoanProcessor } from "@/types/users-loan-processors";
import { serverErrorHandler } from "@/utils/serverErrorHandler";

export const useFetchLoanProcessorProfile = () => {
  const abortController = useRef<AbortController | null>(null);
  const user = useAppStore((s) => s.user);
  const [profile, setProfile] = useState<ILoanProcessor | null>(null);

  const fetchProfile = useCallback(() => {
    if (!user) {
      return;
    }

    const loanProcessorsRepository = new LoanProcessorsRepository(
      new LoanProcessorsService(),
    );

    abortController.current?.abort();
    abortController.current = new AbortController();
    const signal = abortController.current.signal;

    loanProcessorsRepository
      .getProfileByUserId(
        {
          userId: user.id,
        },
        { signal },
      )
      .then((response) => {
        setProfile(response);
      })
      .catch(serverErrorHandler());
  }, [user]);

  useEffect(() => {
    const debouncedFetch = debounce(fetchProfile, 300);
    debouncedFetch();

    return () => {
      debouncedFetch.cancel();
      abortController.current?.abort();
    };
  }, [fetchProfile]);

  return {
    fetchProfile,
    profile,
  };
};
